import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Clock, Calendar, User } from 'lucide-react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { supabase } from '@/integrations/supabase/client';
import CosmicBackground from '@/components/CosmicBackground';
import { Navbar } from '@/components/Layout/Navbar';
import { Footer } from '@/components/Layout/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ReadingProgressBar, 
  TableOfContents, 
  FloatingShareButtons, 
  PrevNextNavigation, 
  BlogBreadcrumbs,
  RelatedPosts,
  BlogSocialLinks 
} from '@/components/Blog';

interface BlogPostData {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  featured_image: string | null;
  category: string;
  tags: string[] | null;
  published_at: string | null;
  reading_time_minutes: number | null;
  meta_title: string | null;
  meta_description: string | null;
  og_image: string | null;
  views: number | null;
  profiles: {
    username: string;
    avatar_id: number | null;
  } | null;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          profiles:author_id (
            username,
            avatar_id
          )
        `)
        .eq('slug', slug)
        .eq('is_published', true)
        .single();
      
      if (error) throw error;
      return data as BlogPostData;
    },
    enabled: !!slug,
  });

  // Increment view count
  useEffect(() => {
    if (post?.id) {
      supabase
        .from('blog_posts')
        .update({ views: (post.views || 0) + 1 })
        .eq('id', post.id)
        .then();
    }
  }, [post?.id]);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const blogPostSchema = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description || post.excerpt,
    "image": post.og_image || post.featured_image,
    "author": {
      "@type": "Person",
      "name": post.profiles?.username || 'ThePromptSpace Team'
    },
    "publisher": {
      "@type": "Organization",
      "name": "ThePromptSpace",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "mainEntityOfPage": currentUrl,
    "wordCount": post.content?.split(/\s+/).length || 0
  } : null;

  if (error) {
    return (
      <div className="relative min-h-screen">
        <CosmicBackground />
        <Navbar />
        <div className="relative z-10 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Post not found</h1>
            <p className="text-white/60">The blog post you're looking for doesn't exist.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ReadingProgressBar />
      <CosmicBackground />
      <Navbar />
      
      {/* Readability Overlay */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] -z-5 pointer-events-none" />

      {post && (
        <>
          <Helmet>
            <title>{post.meta_title || post.title} | ThePromptSpace Blog</title>
            <meta name="description" content={post.meta_description || post.excerpt} />
            <meta property="og:title" content={post.meta_title || post.title} />
            <meta property="og:description" content={post.meta_description || post.excerpt} />
            <meta property="og:image" content={post.og_image || post.featured_image || ''} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={currentUrl} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={post.meta_title || post.title} />
            <meta name="twitter:description" content={post.meta_description || post.excerpt} />
            <meta name="twitter:image" content={post.og_image || post.featured_image || ''} />
            <script type="application/ld+json">
              {JSON.stringify(blogPostSchema)}
            </script>
          </Helmet>

          <FloatingShareButtons url={currentUrl} title={post.title} />
        </>
      )}

      <main className="relative z-10 pt-24 pb-32 lg:pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {isLoading ? (
            <LoadingSkeleton />
          ) : post ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Breadcrumbs */}
              <BlogBreadcrumbs category={post.category} title={post.title} />

              {/* Featured Image */}
              {post.featured_image && (
                <figure className="mb-8">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full aspect-video object-cover rounded-xl border border-white/10"
                  />
                </figure>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display 
                bg-gradient-to-r from-[#00C2FF] via-[#7000FF] to-[#FF00A8] 
                bg-clip-text text-transparent leading-tight mb-6">
                {post.title}
              </h1>

              {/* Author Row */}
              <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm mb-8 pb-8 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#00C2FF]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#00C2FF]" />
                  </div>
                  <span className="text-white/90 font-medium">
                    {post.profiles?.username || 'ThePromptSpace Team'}
                  </span>
                </div>
                {post.published_at && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(post.published_at), 'MMM d, yyyy')}
                  </div>
                )}
                {post.reading_time_minutes && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {post.reading_time_minutes} min read
                  </div>
                )}
              </div>

              {/* Content Layout */}
              <div className="lg:grid lg:grid-cols-[1fr_220px] gap-10">
                <div>
                  {/* Mobile TOC */}
                  <TableOfContents content={post.content} />

                  {/* Article Container */}
                  <article className="
                    bg-black/40 backdrop-blur-md 
                    border border-white/10 
                    rounded-2xl p-6 md:p-10
                    shadow-[0_0_40px_rgba(0,194,255,0.05)]
                  ">
                    <div 
                      className="prose prose-invert prose-lg max-w-none
                        prose-headings:font-display prose-headings:text-white
                        prose-h2:text-2xl prose-h2:border-b prose-h2:border-[#00C2FF]/30 prose-h2:pb-3 prose-h2:mb-6
                        prose-h2:shadow-[0_2px_15px_rgba(0,194,255,0.15)]
                        prose-h3:text-xl prose-h3:text-white/90
                        prose-p:text-white/85 prose-p:leading-relaxed
                        prose-a:text-[#00C2FF] prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-white prose-strong:font-semibold
                        prose-code:text-[#FF00A8] prose-code:bg-white/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                        prose-pre:bg-black/60 prose-pre:backdrop-blur-md 
                        prose-pre:border prose-pre:border-[#00C2FF]/20
                        prose-pre:shadow-[0_0_20px_rgba(0,194,255,0.1)]
                        prose-pre:rounded-xl
                        prose-blockquote:border-l-4 prose-blockquote:border-[#00C2FF]
                        prose-blockquote:bg-white/5 prose-blockquote:backdrop-blur-sm
                        prose-blockquote:pl-6 prose-blockquote:py-4 prose-blockquote:rounded-r-xl
                        prose-blockquote:shadow-[0_0_20px_rgba(0,194,255,0.08)]
                        prose-blockquote:not-italic prose-blockquote:text-white/80
                        prose-img:rounded-xl prose-img:border prose-img:border-white/10
                        prose-ul:text-white/85 prose-ol:text-white/85
                        prose-li:marker:text-[#00C2FF]
                      "
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
                    />
                  </article>

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-8">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1.5 text-xs font-medium rounded-full 
                            bg-white/5 border border-white/10 text-white/70
                            hover:border-[#00C2FF]/40 hover:text-[#00C2FF] transition-colors cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Prev/Next Navigation */}
                  <PrevNextNavigation 
                    currentPostId={post.id} 
                    publishedAt={post.published_at || ''} 
                  />

                  {/* Social Links CTA */}
                  <BlogSocialLinks />

                </div>

                {/* Desktop TOC Sidebar */}
                <aside className="hidden lg:block">
                  <TableOfContents content={post.content} />
                </aside>
              </div>

              {/* Related Posts */}
              <section className="mt-16">
                <h2 className="text-2xl font-bold font-display text-white mb-8">
                  Related Articles
                </h2>
                <RelatedPosts 
                  currentPostId={post.id} 
                  category={post.category} 
                  tags={post.tags || []} 
                />
              </section>
            </motion.div>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-4 w-48 bg-white/10" />
    <Skeleton className="w-full aspect-video rounded-xl bg-white/10" />
    <Skeleton className="h-12 w-3/4 bg-white/10" />
    <div className="flex gap-4">
      <Skeleton className="h-10 w-10 rounded-full bg-white/10" />
      <Skeleton className="h-4 w-32 bg-white/10" />
      <Skeleton className="h-4 w-24 bg-white/10" />
    </div>
    <div className="bg-black/40 rounded-2xl p-8 space-y-4">
      <Skeleton className="h-4 w-full bg-white/10" />
      <Skeleton className="h-4 w-full bg-white/10" />
      <Skeleton className="h-4 w-3/4 bg-white/10" />
    </div>
  </div>
);

export default BlogPost;
